import React, { useCallback } from "react";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { DOMParser, DOMSerializer, Node as ProseNode } from "prosemirror-model";
import uuid from "uuid";

import { EditorConfig, ProseReact } from "~/lib/prose";
import { resetState, TypeAheadPluginKey } from "~/lib/prose/plugins/typeahead";
import { isCtrlOrCmd } from "~/lib/utils";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import { Button } from "~/ui";

import CommentsEditor from "../components/CommentEditor";

import styles from "./UpdateDescription.module.css";

const EMPTY_DOC = {
	type: "doc",
	content: [
		{
			type: "unstyled",
			attrs: {
				assignees: [],
				creator: null,
				blockId: uuid(),
				indent: 0,
				done: false,
			},
		},
	],
};

interface UpdateDescriptionProps {
	initialDescription?: string;
	initialHTML?: string;
	disabled?: boolean;
	onSubmit(text: string, json: {}, html: string): void;
	className?: string;
	contributors: ObjectiveAttributes["contributors"];
}

export default function UpdateDescription({
	initialDescription,
	initialHTML,
	disabled,
	onSubmit,
	className,
	contributors,
}: UpdateDescriptionProps) {
	const [content, setContent] = React.useState<ProseNode | { [key: string]: unknown }>(EMPTY_DOC);
	const editorConfigRef = React.useRef<EditorConfig>();
	const editorRef = React.useRef<ProseReact>(null);

	React.useEffect(() => {
		if (!initialDescription || !initialHTML) return;

		if (initialHTML && editorConfigRef.current) {
			const container = document.createElement("div");
			container.innerHTML = initialHTML;
			const node = DOMParser.fromSchema(editorConfigRef.current.schema).parse(container);
			setContent(node);
		} else {
			setContent(existingContent => {
				if (existingContent.textContent) return existingContent;
				return {
					type: "doc",
					content: [
						{
							type: "unstyled",
							attrs: {
								assignees: [],
								creator: null,
								blockId: uuid(),
								indent: 0,
								done: false,
							},
							content: [{ type: "text", text: initialDescription }],
						},
					],
				};
			});
		}
	}, [setContent, initialDescription, initialHTML]);

	const onConfirm = React.useCallback(() => {
		const commentContent = editorRef.current?.editorView?.state.doc;
		if (!editorConfigRef.current || !editorRef || !editorRef.current || !(commentContent instanceof ProseNode)) return;

		if (commentContent.textContent === "") return;

		const editorView = editorRef.current?.editorView;
		if (editorView) {
			const editorState = editorView.state;
			if (editorState) {
				const typeaheadState = TypeAheadPluginKey.getState(editorState);
				if (typeaheadState) {
					editorView.dispatch(editorView.state.tr.setMeta(TypeAheadPluginKey, resetState(typeaheadState)));
				}
			}
		}

		const text = commentContent.textBetween(1, commentContent.nodeSize - 2, "\n", "\n");
		const json = commentContent.toJSON();
		const html = DOMSerializer.fromSchema(editorConfigRef.current.schema).serializeFragment(commentContent.content);
		const container = document.createElement("div");
		container.appendChild(html);

		onSubmit(text, json, container.innerHTML);
	}, [editorRef, onSubmit]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (isCtrlOrCmd(e.nativeEvent) && e.key == "Enter") {
				onConfirm();
			}
		},
		[onConfirm],
	);

	return (
		<div className={cx(styles.editCommentContainer)}>
			<div className={cx(styles.comment, className)} onKeyDown={onKeyDown}>
				<CommentsEditor
					onChange={setContent}
					doc={content}
					placeholder="Add a comment…"
					configRef={editorConfigRef}
					editorRef={editorRef}
					contributors={contributors}
				/>
			</div>

			<Button
				size="wide"
				onClick={onConfirm}
				className={cx(styles.commentActionButton)}
				variant="primary"
				disabled={disabled || content.textContent === ""}
			>
				<FontAwesomeIcon icon={faCheck} fixedWidth style={{ margin: 0 }} />
			</Button>
		</div>
	);
}
